import { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Alert,
  ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { getTeacherWeeklyTimetable } from '@/api/teacher-api';

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'] as const;
const DAY_LABELS: Record<string, string> = {
  MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed',
  THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat',
};
const TODAY_DAY = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][new Date().getDay()];

export function TeacherTimetablePage() {
  const user = useAuthStore((state) => state.user);
  const [view, setView] = useState<'week' | 'day'>('week');
  const [selectedDay, setSelectedDay] = useState<string>(TODAY_DAY);

  const { data, isLoading, error } = useQuery({
    queryKey: ['teacher-timetable', user?.id],
    queryFn: () => getTeacherWeeklyTimetable(user!.id),
    enabled: !!user?.id,
  });

  if (isLoading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">Could not load timetable.</Alert>;
  if (!data) return null;

  const periods = data.periods; // sorted by period_number
  const records = data.records; // TimetableRecord[]

  const getCell = (day: string, periodId: string) =>
    records.find((r) => r.day_of_week === day && r.period_id === periodId);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>My Timetable</Typography>
          <Typography variant="body2" color="text.secondary">{data.academicYear}</Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="day">Day</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {view === 'day' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {DAYS.map((d) => (
            <Chip
              key={d}
              label={DAY_LABELS[d]}
              onClick={() => setSelectedDay(d)}
              color={selectedDay === d ? 'primary' : 'default'}
              variant={selectedDay === d ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      )}

      {view === 'week' ? (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 80 }}>Period</TableCell>
                {DAYS.map((d) => (
                  <TableCell key={d} align="center" sx={{ fontWeight: 700, bgcolor: d === TODAY_DAY ? 'primary.50' : undefined }}>
                    {DAY_LABELS[d]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>P{p.period_number}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {p.time_start.slice(0, 5)}
                    </Typography>
                  </TableCell>
                  {DAYS.map((d) => {
                    const cell = getCell(d, p.id);
                    return (
                      <TableCell key={d} align="center" sx={{ bgcolor: d === TODAY_DAY ? 'primary.50' : undefined, minWidth: 100 }}>
                        {cell ? (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                              {(cell as any).subject?.subject_name ?? '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(cell as any).class_section?.class?.class_name} {(cell as any).class_section?.section}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {periods.map((p) => {
            const cell = getCell(selectedDay, p.id);
            return (
              <Card key={p.id} variant="outlined" sx={{ borderRadius: 2, opacity: cell ? 1 : 0.45 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {cell ? ((cell as any).subject?.subject_name ?? 'No Subject') : 'Free Period'}
                      </Typography>
                      {cell && (
                        <Typography variant="body2" color="text.secondary">
                          {(cell as any).class_section?.class?.class_name} {(cell as any).class_section?.section}
                        </Typography>
                      )}
                    </Box>
                    <Chip size="small" label={`${p.time_start.slice(0, 5)} – ${p.time_end.slice(0, 5)}`} variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
